import type { MenuJsonType } from './Menu'
import CurvedCorner from './CurvedCorner'
import type { LinkCompType } from './navUtils'
import { useEffect, useRef, useCallback } from 'react'

export function MobileL1Nav({
  menuJson,
  mobileNavOpen,
  mobileScrolledDown,
  setMobileScrolledDown,
  LinkComp,
}: {
  menuJson: MenuJsonType
  mobileNavOpen: boolean
  mobileScrolledDown: boolean
  setMobileScrolledDown: (s: boolean) => void
  LinkComp: LinkCompType
}) {
  const prevY = useRef(0)
  const dirY = useRef('up')
  const disableScrollCheck = useRef(false)
  const changeTimeout = useRef(0)

  const openL1 = useCallback(
    (resetScrollCheck = false) => {
      const posY = Math.max(0, window.scrollY)
      dirY.current = 'up'
      prevY.current = posY
      disableScrollCheck.current = true
      window.clearTimeout(changeTimeout.current)
      setMobileScrolledDown(false)

      changeTimeout.current = window.setTimeout(() => {
        prevY.current = posY
        if (resetScrollCheck) disableScrollCheck.current = false
      }, 1000)
    },
    [setMobileScrolledDown],
  )

  //hide L1 on scroll down, show L1 on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const posY = Math.max(0, window.scrollY)
      if (posY == 0) openL1(true)
      let isChanged = false
      let disabled = disableScrollCheck.current

      //don't do anything if at end of page
      const scrollEnd = document.body.scrollHeight - window.innerHeight - 100
      if (posY >= scrollEnd) {
        disabled = true
      }

      if (!disabled) {
        //down
        if (prevY.current < posY && dirY.current !== 'down') {
          dirY.current = 'down'
          isChanged = true
        }
        //up
        if (prevY.current > posY && dirY.current !== 'up') {
          dirY.current = 'up'
          isChanged = true
        }
      }

      prevY.current = posY

      //timeout if changed scroll direction
      if (isChanged) {
        setMobileScrolledDown(dirY.current == 'down' ? true : false)
        disableScrollCheck.current = true
        changeTimeout.current = window.setTimeout(() => {
          prevY.current = posY
          disableScrollCheck.current = false
        }, 1000)
      }
    }

    prevY.current = window.scrollY
    dirY.current = 'up'
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [openL1, setMobileScrolledDown])

  //open L1 when mobile menu opened
  useEffect(() => {
    if (mobileNavOpen) {
      openL1()
    } else {
      disableScrollCheck.current = false
    }
  }, [mobileNavOpen, openL1])

  return (
    <>
      {/* L1 navigation - mobile */}
      <div
        data-mobile-l1
        className={`block transition-all duration-500 ease-in-out lg:hidden ${mobileScrolledDown ? 'h-0 opacity-0' : 'h-[42px] opacity-100'}`}
      >
        <div className="h-1 w-full bg-[var(--nav-accent)]"></div>
        <ul className="flex justify-center gap-0 px-4">
          {Object.keys(menuJson).map((key) => {
            const L1 = menuJson[key]
            return (
              <li key={key} className="flex" data-l1-key={key}>
                <LinkComp
                  className={`nav-level-1 relative flex h-[35px] items-center px-3 text-black`}
                  href={L1?.url ?? '#'}
                >
                  <div
                    data-l1-tab-bg
                    className={`absolute left-0 top-0 h-[100%] w-full shrink-0 rounded-b-xl bg-[var(--nav-accent)] fill-[var(--nav-accent)] opacity-0`}
                  >
                    <CurvedCorner
                      classes={{
                        fill: '',
                        size: 'w-3 h-3',
                        container: 'absolute top-0 right-full -scale-y-100',
                      }}
                    />
                    <CurvedCorner
                      classes={{
                        fill: '',
                        size: 'w-3 h-3',
                        container:
                          'absolute top-0 left-full -scale-x-100 -scale-y-100',
                      }}
                    />
                  </div>
                  <span className="relative">{L1?.label}</span>
                </LinkComp>
              </li>
            )
          })}
        </ul>
      </div>
    </>
  )
}
